import React, { FC } from 'react';
import { Grid, Container, Box, Button, makeStyles, useTheme } from '@dxb-web-ui-framework/design-system';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { ArrowBack as ArrowBackIcon } from '@dxb-web-ui-framework/design-system/dist/icons';
import { useNavigate } from 'react-router-dom';
import { HeroContent } from '../../types';
import { CardHeroBannerText } from './CardHeroBannerText';

type CardHeroBannerProps = {
  heroImage?: string;
  heroImageSize?: string;
  moto?: string;
  slogan?: string;
  content?: HeroContent;
  hasBackButton?: boolean;
  onBackOverride?: () => void;
};

const useStyles = makeStyles(({ spacing, breakpoints }) => ({
  containerWrapper: {
    overflowY: 'hidden',
    [breakpoints.down('sm')]: {
      padding: spacing(0, 2, 1, 2),
    },
  },
  contentWrapper: (props: any) => ({
    width: '100%',
    minHeight: 250,
    maxHeight: '100%',
    display: 'flex',
    marginLeft: 100,
    flexDirection: props.flexDirection ? props.flexDirection : 'unset',
    [breakpoints.down('sm')]: {
      minHeight: 180,
      marginLeft: 0,
    },
  }),
  imageContainer: {
    [breakpoints.down('sm')]: {},
  },
  img: ({ heroImageSize }: any) => ({
    width: heroImageSize ?? spacing(19),
    paddingBottom: spacing(5),
    objectFit: 'contain',
    transform: 'scale(1.2)',
    position: 'relative',
    [breakpoints.down('sm')]: {
      transform: 'scale(1)',
      paddingBottom: spacing(0),
    },
  }),
  textContainer: (props: any) => ({
    backgroundColor: props.background ? props.background : 'unset',
  }),
}));

// TODO: add the hero banner bg image and style it to be as per designs with padding and positioning
const CardHeroBanner: FC<CardHeroBannerProps> = ({
  heroImage,
  heroImageSize,
  moto,
  slogan,
  content,
  hasBackButton,
  onBackOverride,
}) => {
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = useStyles({
    background: isMobile
      ? typeof content?.textBgColorMobile === 'object'
        ? // @ts-ignore
          theme.palette[content?.textBgColorMobile.type as any][content?.textBgColorMobile.value as any]
        : content?.textBgColorMobile
      : undefined,
    flexDirection: isMobile ? 'column-reverse' : 'row',
    heroImageSize,
  });

  const shouldTextOutFromContainer = isMobile && content?.textBgColorMobile;
  const shouldShowBackButton = !isMobile && hasBackButton;
  return (
    <>
      <Container maxWidth={isMobile ? 'md' : 'lg'} className={classes.containerWrapper}>
        <Grid container>
          {shouldShowBackButton && (
            <Grid item xs={12}>
              <Box mt={1}>
                <Button
                  aria-label='Back'
                  color='secondary'
                  isIconButton
                  size='small'
                  title='Back'
                  variant='text'
                  data-testid='navigate-back-button'
                  onClick={() => (onBackOverride ? onBackOverride() : navigate(-1))}>
                  <ArrowBackIcon />
                </Button>
              </Box>
            </Grid>
          )}
          <Grid container className={classes.contentWrapper}>
            <Box display={'flex'} flexDirection={'row'} width={'100%'}>
              {!shouldTextOutFromContainer && (
                <Grid item xs={6}>
                  <CardHeroBannerText moto={moto} slogan={slogan} />
                </Grid>
              )}
              <Grid item xs={shouldTextOutFromContainer ? 12 : 6} className={classes.imageContainer}>
                <Box display={'flex'} height='100%' width={'100%'} justifyContent={'center'}>
                  <img src={heroImage} width={'100%'} height='100%' className={classes.img} alt={`${moto} ${slogan}`} />
                </Box>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Container>
      {shouldTextOutFromContainer && (
        <Box className={classes.textContainer}>
          <Box mx={3} py={2}>
            <CardHeroBannerText moto={moto} slogan={slogan} />
          </Box>
        </Box>
      )}
    </>
  );
};

export { CardHeroBanner };
